// frontend/src/components/masters/CustomerList.js
import React, { useState, useEffect, useCallback } from 'react';
import CustomerForm from './CustomerForm';
import useSortableTable from '../common/useSortableTable';
import './CustomerList.css';
import { usePermissions } from '../../services/PermissionsContext'; // 1. Import the permissions hook

const API_BASE_URL = '/api/masters/customers/';

// Helper to get auth token
const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('kk_token')}`
});

const CustomerList = () => {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    // State for managing the CustomerForm modal
    const [showFormModal, setShowFormModal] = useState(false);
    const [formMode, setFormMode] = useState('add'); // 'add', 'edit', 'view'
    const [selectedCustomer, setSelectedCustomer] = useState(null); // Data for edit/view mode

    // State for expandable rows
    const [expandedRows, setExpandedRows] = useState(new Set());

    const { hasPermission } = usePermissions();

    // Fetch customers from API
    const fetchCustomers = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(API_BASE_URL, {
                headers: getAuthHeaders()
            });
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
            }
            const data = await response.json();
            setCustomers(data);
        } catch (err) {
            console.error("Error fetching customers:", err);
            setError('Failed to fetch customers: ' + err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCustomers();
    }, [fetchCustomers]);

    // Filter logic only
    const getFilteredCustomers = () => {
        return customers.filter(c => {
            const query = searchQuery.toLowerCase();
            const hasBankMatch = c.bank_details && c.bank_details.some(
                detail => (detail.bank_name || '').toLowerCase().includes(query) ||
                          (detail.account_number || '').toLowerCase().includes(query)
            );
            const hasDestinationMatch = c.destinations && c.destinations.some(
                dest => (dest.city || '').toLowerCase().includes(query)
            );

            return (
                (c.name || '').toLowerCase().includes(query) ||
                (c.address || '').toLowerCase().includes(query) ||
                (c.tin || '').toLowerCase().includes(query) ||
                (c.vrn || '').toLowerCase().includes(query) ||
                hasBankMatch ||
                hasDestinationMatch
            );
        });
    };

    // Sortable table hook
    const { items: sortedCustomers, requestSort, getSortIndicator } = useSortableTable(getFilteredCustomers());

    // --- Modal Control Functions ---
    const openForm = (mode, customerData = null) => {
        setFormMode(mode);
        setSelectedCustomer(customerData);
        setShowFormModal(true);
    };

    const handleFormClose = (refreshList = false) => {
        setShowFormModal(false);
        setSelectedCustomer(null);
        setFormMode('add');
        if (refreshList) {
            fetchCustomers(); // Re-fetch data if form indicated a save
        }
    };

    // --- Row Expansion Handler ---
    const toggleRowExpansion = (id) => {
        setExpandedRows(prev => {
            const newExpandedRows = new Set(prev);
            if (newExpandedRows.has(id)) {
                newExpandedRows.delete(id);
            } else {
                newExpandedRows.add(id);
            }
            return newExpandedRows;
        });
    };

    // --- CRUD Operations from List ---
    const handleDeleteCustomer = async (id) => {
        if (window.confirm("Are you sure you want to delete this customer? This action cannot be undone.")) {
            setError(null);
            try {
                const response = await fetch(`${API_BASE_URL}${id}/`, {
                    method: 'DELETE',
                    headers: getAuthHeaders()
                });

                if (!response.ok) {
                    const errorText = await response.text();
                    throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
                }
                // If deletion is successful, refetch the list
                fetchCustomers();
            } catch (err) {
                console.error("Error deleting customer:", err);
                setError('Failed to delete customer: ' + err.message);
            }
        }
    };

    const filteredAndSortedCustomers = sortedCustomers;

    return (
        <div className="customer-list-container">
            <h2>Customer List</h2>
            {error && <div className="error-message">{error}</div>}
            <div className="toolbar">
                <div>
                    <input
                        type="text"
                        placeholder="Search by any of the fields..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <button onClick={() => setSearchQuery('')}>Clear</button>
                </div>
                {/* 3. Conditionally render the "Add New" button */}
                {hasPermission('CUSTOMERS', 'can_create') && (
                    <button onClick={() => openForm('add')}>+ Add New Customer</button>
                )}
            </div>

            {loading ? (
                <div className="loading-message">Loading customers...</div>
            ) : (
                <table>
                    <thead>
                        <tr>
                            <th className="sortable" onClick={() => requestSort('name')}>
                                Name {getSortIndicator('name')}
                            </th>
                            <th className="sortable" onClick={() => requestSort('address')}>
                                Address {getSortIndicator('address')}
                            </th>
                            <th className="sortable" onClick={() => requestSort('tin')}>
                                TIN {getSortIndicator('tin')}
                            </th>
                            <th className="sortable" onClick={() => requestSort('vrn')}>
                                VRN {getSortIndicator('vrn')}
                            </th>
                            <th>Details</th>
                            {/* --- FIX: Render Actions column if user has ANY action permission (view, edit, or delete) --- */}
                            {(hasPermission('CUSTOMERS', 'can_view') || hasPermission('CUSTOMERS', 'can_edit') || hasPermission('CUSTOMERS', 'can_delete')) && (
                                <th>Actions</th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {filteredAndSortedCustomers.length === 0 ? (
                            <tr>
                                <td colSpan="6" className="no-data">No customers found.</td>
                            </tr>
                        ) : (
                            filteredAndSortedCustomers.map((customer) => (
                                <React.Fragment key={customer.id}>
                                    <tr>
                                        <td>{customer.name}</td>
                                        <td>{customer.address},{customer.city},{customer.country}</td>
                                        <td>{customer.tin}</td>
                                        <td>{customer.vrn}</td>
                                        <td className="expand-toggle">
                                            {(customer.bank_details?.length > 0 || customer.destinations?.length > 0) && (
                                                <button
                                                    className="icon-btn"
                                                    onClick={() => toggleRowExpansion(customer.id)}
                                                    title={expandedRows.has(customer.id) ? 'Collapse Details' : 'Expand Details'}
                                                >
                                                    <i className={`fa ${expandedRows.has(customer.id) ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
                                                </button>
                                            )}
                                        </td>
                                        {/* --- FIX: Render the actions cell with the same condition as the header --- */
                                        (hasPermission('CUSTOMERS', 'can_view') || hasPermission('CUSTOMERS', 'can_edit') || hasPermission('CUSTOMERS', 'can_delete')) && (
                                            <td className="actions">
                                                {/* Conditionally render View button based on 'can_view' permission */}
                                                {hasPermission('CUSTOMERS', 'can_view') && (
                                                    <button className="icon-btn" title="View" onClick={() => openForm('view', customer)}>
                                                        <i className="fa fa-eye"></i>
                                                    </button>
                                                )}
                                                {/* Conditionally render Edit button based on 'can_edit' permission */}
                                                {hasPermission('CUSTOMERS', 'can_edit') && (
                                                    <button className="icon-btn" title="Edit" onClick={() => openForm('edit', customer)}>
                                                        <i className="fa fa-pen"></i>
                                                    </button>
                                                )}
                                                {/* Conditionally render Delete button based on 'can_delete' permission */}
                                                {hasPermission('CUSTOMERS', 'can_delete') && (
                                                    <button className="icon-btn delete-btn" title="Delete" onClick={() => handleDeleteCustomer(customer.id)}>
                                                        <i className="fa fa-trash"></i>
                                                    </button>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                    {expandedRows.has(customer.id) && (
                                        <tr className="nested-details-row">
                                            <td colSpan="6">
                                                <div className="nested-details-content">
                                                    <div className="nested-details-column">
                                                        <h4>Bank Accounts</h4>
                                                        {customer.bank_details && customer.bank_details.length > 0 ? (
                                                            <table className="details-table">
                                                                <thead>
                                                                    <tr>
                                                                        <th>Bank</th>
                                                                        <th>Swift</th>
                                                                        <th>Currency</th>
                                                                        <th>Account #</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {customer.bank_details.map(detail => (
                                                                        <tr key={detail.id}>
                                                                            <td>{detail.bank_name.toUpperCase()}</td>
                                                                            <td>{detail.swift_code}</td>
                                                                            <td>{detail.currency}</td>
                                                                            <td>{detail.account_number}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        ) : <p>No bank accounts found.</p>}
                                                    </div>
                                                    {customer.destinations && customer.destinations.length > 0 && (
                                                        <div className="nested-details-column">
                                                            <h4>Destinations</h4>
                                                            <ol className="details-list ordered-list">
                                                                {customer.destinations.map(dest => (
                                                                    <li key={dest.id}>{dest.city}</li>
                                                                ))}
                                                            </ol>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))
                        )}
                    </tbody>
                </table>
            )}

            {/* CustomerForm Modal */}
            {showFormModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <button className="modal-close-btn" onClick={() => handleFormClose(false)} title="Close Form">&times;</button>
                        <CustomerForm
                            mode={formMode}
                            initialData={selectedCustomer}
                            onClose={(refreshList) => handleFormClose(refreshList)}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomerList;
